import React, { useState } from "react";
import { Wrench, CalendarClock, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/**
 * Outil de réparation : recalcule la DLC des lots actifs ayant une dlc vide.
 *
 * Cause typique : OFs créés avec des lignes sans DLC saisie (ou via le bouton
 * « Mettre en fabrication » avant la Phase 43). Au moment où l'OF est clôturé,
 * les lots créés héritent d'une dlc=null, ce qui rend les BL illisibles et
 * casse la traçabilité HACCP.
 *
 * Formule de recalcul : DLC = production_date (ou created_at) + product.shelf_life_days
 *
 * Workflow :
 *  1. Aperçu (dry-run) : liste les lots à corriger
 *  2. Application : applique le fix sur les lots listés
 */
export default function FixMissingDlcDialog() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setPreview(null);
    setDone(false);
  };

  const runPreview = async () => {
    setLoading(true);
    setDone(false);
    try {
      const { data } = await api.post("/admin/fix-missing-dlc?dry_run=true");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'aperçu");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/fix-missing-dlc?dry_run=false");
      toast.success(`${data.fixable_count} lot(s) corrigé(s)`);
      setDone(true);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'application");
    } finally {
      setApplying(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
        data-testid="open-fix-dlc-dialog"
      >
        <Wrench className="w-4 h-4 mr-2" />
        Réparer les DLC manquantes sur les lots
      </Button>
      <AlertDialogContent className="max-w-2xl" data-testid="fix-dlc-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#8C6B2F]" />
            Réparer les DLC manquantes
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2 text-left">
            <span className="block">
              Scanne tous les lots actifs (quantité &gt; 0) dont la DLC est vide.
              La DLC est recalculée via : <code className="text-xs bg-[#F4F4F0] px-1 py-0.5 rounded">production_date + shelf_life_days du produit</code>.
            </span>
            <span className="block text-[#8C6B2F]">
              Cas typique : OFs créés avant Phase 43 sans DLC saisie. Les BL générés à partir
              de ces lots affichent « — » au lieu de la DLC.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!preview && !loading && (
          <div className="border border-[#E5E5E0] rounded p-4 bg-[#FAFAF7] text-sm text-[#575A57]">
            Cliquez sur « Aperçu » pour voir la liste des lots qui seraient corrigés.
            Aucune modification n'est apportée tant que vous n'appliquez pas explicitement.
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-6 text-[#575A57]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analyse en cours…
          </div>
        )}

        {preview && (
          <div className="space-y-3" data-testid="fix-dlc-preview">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-[#E5E5E0] rounded p-3 bg-white">
                <div className="text-xs text-[#A3A6A3] uppercase tracking-wide">Lots scannés</div>
                <div className="text-xl font-mono font-medium text-[#1A1C1A]">{preview.scanned}</div>
              </div>
              <div className="border border-[#C5DBCF] rounded p-3 bg-[#EAF4EE]">
                <div className="text-xs text-[#1F4E3D] uppercase tracking-wide">Réparables</div>
                <div className="text-xl font-mono font-medium text-[#1F4E3D]">{preview.fixable_count}</div>
              </div>
              <div className="border border-[#E8D3A8] rounded p-3 bg-[#FBF4E9]">
                <div className="text-xs text-[#8C6B2F] uppercase tracking-wide">Ignorés</div>
                <div className="text-xl font-mono font-medium text-[#8C6B2F]">{preview.skipped_count}</div>
              </div>
            </div>

            {done && (
              <div className="border border-[#C5DBCF] bg-[#EAF4EE] rounded p-3 flex items-center gap-2 text-sm text-[#1F4E3D]">
                <CheckCircle2 className="w-4 h-4" />
                Correction appliquée. Rechargez votre BL pour voir les DLC mises à jour.
              </div>
            )}

            {preview.preview && preview.preview.length > 0 && (
              <div className="border border-[#E5E5E0] rounded max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F4F4F0] sticky top-0">
                    <tr className="text-[#575A57]">
                      <th className="text-left p-2">Lot</th>
                      <th className="text-left p-2">Produit</th>
                      <th className="text-right p-2">Qté</th>
                      <th className="text-left p-2">Réf. date</th>
                      <th className="text-right p-2">+ jours</th>
                      <th className="text-left p-2">Nouvelle DLC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((item) => (
                      <tr key={item.lot_id} className="border-t border-[#E5E5E0]">
                        <td className="p-2 font-mono text-[10px]">{item.lot_number || "—"}</td>
                        <td className="p-2">{item.product_name} <span className="text-[#A3A6A3]">{item.product_sku ? `(${item.product_sku})` : ""}</span></td>
                        <td className="p-2 text-right font-mono">{item.quantity}</td>
                        <td className="p-2 text-[10px]">{fmtDate(item.ref_date)}</td>
                        <td className="p-2 text-right font-mono">{item.shelf_life_days}j</td>
                        <td className="p-2 font-medium text-[#1F4E3D]">{fmtDate(item.new_dlc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.fixable_count > preview.preview.length && (
                  <div className="p-2 text-xs text-[#A3A6A3] text-center bg-[#FAFAF7]">
                    ({preview.fixable_count - preview.preview.length} autre(s) lot(s) non affiché(s) — aperçu tronqué)
                  </div>
                )}
              </div>
            )}

            {preview.skipped && preview.skipped.length > 0 && (
              <div className="border border-[#E8D3A8] rounded p-3 bg-[#FBF4E9]/40">
                <div className="text-xs text-[#8C6B2F] font-medium mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Lots ignorés (cause généralement : shelf_life_days manquant sur le produit)
                </div>
                <ul className="text-[11px] text-[#5C4A1F] space-y-0.5 max-h-24 overflow-y-auto">
                  {preview.skipped.slice(0, 20).map((s) => (
                    <li key={s.lot_id}>
                      <span className="font-mono">{s.lot_number || "—"}</span> · {s.product_name} → {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter className="gap-2 mt-2">
          <AlertDialogCancel data-testid="fix-dlc-cancel">Fermer</AlertDialogCancel>
          {!preview && (
            <Button onClick={runPreview} disabled={loading} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="fix-dlc-preview-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Aperçu
            </Button>
          )}
          {preview && !done && preview.fixable_count > 0 && (
            <AlertDialogAction
              onClick={apply}
              disabled={applying}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="fix-dlc-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Appliquer la correction sur {preview.fixable_count} lot(s)
            </AlertDialogAction>
          )}
          {preview && !done && preview.fixable_count === 0 && (
            <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]">
              Rien à corriger
            </Badge>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
